import { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { debounce } from 'underscore';
import { TYPING_DEBOUNCE_MS } from '../editor/constants';
import styles from './content-editable.module.css';
import { QuillInactiveContext } from '@ui/quill/quill-inactive-context';

// clearing a contenteditable leaves a lone <br>, which innerText reads as a newline - that's no text
const readText = (el: HTMLElement) => (el.innerText === '\n' ? '' : el.innerText);

interface Props extends React.HTMLProps<HTMLDivElement> {
  value: string | undefined;
  setValue: ((str: string) => void) | undefined;
  // in the editor, an emptied field keeps a line's height so it can still be hovered and clicked into -
  // exactly one line (1lh), the height the browser gives it anyway once hovering makes it editable
  keepLineWhenEmpty?: boolean;
}
export default function ContentEditable({ value, setValue, keepLineWhenEmpty, style, ...rest }: Props) {
  const quillState = useContext(QuillInactiveContext);

  const ref = useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [editable, setEditable] = useState(false);

  // the element already shows what's typed, so the edit only lands once typing pauses
  const setValueRef = useRef(setValue);
  useEffect(() => {
    setValueRef.current = setValue;
  }, [setValue]);
  const commit = useRef(debounce((text: string) => setValueRef.current?.(text), TYPING_DEBOUNCE_MS)).current;

  useEffect(() => {
    if (value !== undefined && !focused && ref.current && readText(ref.current) !== value) {
      ref.current.innerText = value;
    }
  }, [value, focused]);

  if (quillState.hidden) {
    return (
      <div {...rest} style={style}>
        {value}
      </div>
    );
  }

  return (
    <div
      {...rest}
      ref={ref}
      style={
        setValue
          ? { ...(keepLineWhenEmpty ? { minHeight: '1lh' } : {}), ...style }
          : { ...style, cursor: 'default', userSelect: 'none' }
      }
      className={
        classNames(editable && styles.contentEditable, keepLineWhenEmpty && setValue && !value && styles.empty) ||
        undefined
      }
      contentEditable={editable ? true : undefined}
      suppressContentEditableWarning
      onInput={() => {
        if (ref.current) {
          commit(readText(ref.current));
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const inserted =
          document.queryCommandSupported?.('insertText') && document.execCommand('insertText', false, text);
        if (!inserted) {
          const selection = window.getSelection();
          if (selection?.rangeCount) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(text));
            range.collapse(false);
          }
          commit(ref.current ? readText(ref.current) : '');
        }
      }}
      onPointerEnter={() => {
        if (setValue) setEditable(true);
      }}
      onPointerLeave={() => {
        if (!focused) {
          setEditable(false);
        }
      }}
      onFocus={() => {
        if (setValue) setFocused(true);
      }}
      onBlur={() => {
        // lands whatever is still waiting, so the text can't snap back to the older value on blur
        commit.cancel();
        const text = ref.current ? readText(ref.current) : undefined;
        if (text !== undefined && text !== value) setValue?.(text);
        setFocused(false);
        setEditable(false);
      }}
    />
  );
}
